import { useState, type ReactNode } from "react";
import { View } from "react-native";
import { Button } from "~/components/ui/Button";
import { Screen } from "~/components/ui/Screen";
import { BodyText, Heading2 } from "~/components/ui/Text";
import { TextField } from "~/components/ui/TextField";
import { useAuth } from "~/hooks/useAuth";
import { useMfaState, verifyTotpCode } from "~/lib/totp";
import { spacing, useTheme } from "~/styles/theme";

function MfaChallenge() {
  const { colors } = useTheme();
  const { signOut, refresh } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await verifyTotpCode(code);
      await refresh();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "That code did not work.",
      );
      setBusy(false);
    }
  };

  return (
    <Screen>
      <View
        style={{
          flex: 1,
          width: "100%",
          maxWidth: 440,
          alignSelf: "center",
          justifyContent: "center",
          padding: spacing.lg,
          gap: spacing.lg,
        }}
      >
        <Heading2>Two-factor authentication</Heading2>
        <BodyText color={colors.muted}>
          Enter the 6-digit code from your authenticator app to finish signing
          in.
        </BodyText>
        <TextField
          label="6-digit code"
          value={code}
          onChangeText={setCode}
          keyboardType="number-pad"
          autoComplete="one-time-code"
          maxLength={6}
        />
        {error ? <BodyText color={colors.danger}>{error}</BodyText> : null}
        <Button
          label={busy ? "Checking…" : "Verify"}
          disabled={busy || code.trim().length !== 6}
          fullWidth
          onPress={() => void submit()}
        />
        <Button
          label="Sign out"
          variant="secondary"
          fullWidth
          onPress={() => void signOut()}
        />
      </View>
    </Screen>
  );
}

/**
 * Holds the app back until a signed-in person who turned on two-factor
 * authentication has entered their code. Everyone else passes straight through.
 */
export function MfaGate({ children }: { children: ReactNode }) {
  const state = useMfaState();
  if (state === "checking") return null;
  if (state === "required") return <MfaChallenge />;
  return <>{children}</>;
}
